import React, { memo, useMemo } from 'react';
import LiftIcon from './icons/LiftIcon';
import RunIcon from './icons/RunIcon';
import { activityAt, buildActivityStrip } from '../utils/dayActivity';
import { formatInTimeZone, getBrowserTimeZone } from '../utils/timezone';
import './DayActivityStrip.css';

const RUN_LABELS = {
  green: 'Green',
  blue: 'Blue',
  red: 'Red',
  black: 'Black',
  double_black: 'Double black',
  grey: 'Terrain park',
  orange: 'Freeride',
  yellow: 'Extreme'
};

const fmtTime = (ms) => formatInTimeZone(ms, getBrowserTimeZone(), { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });

const fmtDuration = (ms) => {
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} hr ${minutes % 60} min`;
};

/** The class that colors a stretch: its kind, and for a run its difficulty. */
const colorClass = ({ kind, feature }) => (kind === 'run' ? `run-${feature.colorName}` : kind);

const kindLabel = ({ kind, feature }) => {
  if (kind === 'idle') return 'Idle';
  if (kind === 'lift') return 'Lift';
  if (kind === 'run') return RUN_LABELS[feature.colorName] ?? 'Run';
  return 'Moving';
};

/** What the skier was doing, named when the map knows the lift or run. */
const stretchLabel = (stretch) => stretch.feature?.name || kindLabel(stretch);

const StretchIcon = ({ stretch }) => {
  if (stretch.kind === 'lift') return <LiftIcon liftType={stretch.feature.liftType} />;
  if (stretch.kind === 'run') return <RunIcon colorName={stretch.feature.colorName} />;
  if (stretch.kind === 'idle') return <span className="day-activity-zzz">Zzz</span>;
  return null;
};

/** Separate from the playhead so playback doesn't re-render every segment each tick. */
const StripSegments = memo(({ segments }) => (
  <>
    {segments.map((s) => (
      <div
        key={s.startMs}
        className={`day-activity-segment ${colorClass(s)}`}
        style={{ left: `${s.left}%`, width: `${s.width}%` }}
        title={`${stretchLabel(s)} ${fmtTime(s.startMs)} – ${fmtTime(s.endMs)} · ${fmtDuration(s.endMs - s.startMs)}`}
      >
        <span className="day-activity-mark"><StretchIcon stretch={s} /></span>
      </div>
    ))}
  </>
));

/** One entry per color on the strip this day. */
const Legend = memo(({ segments }) => {
  const entries = [...new Map(segments.map((s) => [colorClass(s), s])).values()];
  return (
    <div className="day-activity-legend">
      {entries.map((s) => (
        <span key={colorClass(s)} className={`day-activity-legend-item ${colorClass(s)}`}>
          {s.kind === 'lift' || s.kind === 'run' ? <StretchIcon stretch={s} /> : <span className="day-activity-swatch" />}
          {kindLabel(s)}
        </span>
      ))}
    </div>
  );
});

/**
 * What the skier was doing through the day, lined up with the playback slider
 * underneath it: both run from the first to the last recorded point.
 *
 * @param {Array} stretches - from deriveDayActivity
 * @param {number} trackStartMs - First recorded point
 * @param {number} trackEndMs - Last recorded point
 * @param {number} currentMs - Playback position
 */
const DayActivityStrip = ({ stretches, trackStartMs, trackEndMs, currentMs }) => {
  const segments = useMemo(() => buildActivityStrip(stretches, trackStartMs, trackEndMs), [stretches, trackStartMs, trackEndMs]);
  const current = activityAt(stretches, currentMs);
  const playhead = ((currentMs - trackStartMs) / Math.max(1, trackEndMs - trackStartMs)) * 100;

  return (
    <div className="day-activity">
      <div className="day-activity-head">
        <span>Activity</span>
        <Legend segments={segments} />
        <span className={`day-activity-state ${current ? colorClass(current) : 'no-data'}`}>
          {(current?.kind === 'lift' || current?.kind === 'run') && <StretchIcon stretch={current} />}
          {current ? stretchLabel(current) : 'No data'}
        </span>
      </div>
      <div className="day-activity-track">
        <StripSegments segments={segments} />
        <div className="day-activity-playhead" style={{ left: `${playhead}%` }} />
      </div>
    </div>
  );
};

export default DayActivityStrip;
